import React, { useEffect, useState } from 'react'
import { Button, Form, Input, message, Radio, Select, Upload } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { fetchBrandList } from '../../../apis/brand'
import { readCsvToJson } from '../../../components/utils/readExcel'
import { useLocation } from 'react-router-dom'
import { fetchSchema } from '../../../apis/source'
import { SnapshotExtraSchemas } from '../../../constants/Sources'

const acceptedUploadFormat = '.csv, text/csv'
function BasicSetting (props) {
  const { form, defConfig, defPlaceholder, sources, editMode, setTables, conf, defParams, setTableData, tableData, typeOptions, setTypeOptions } = props
  const { t: ln_t } = useTranslation()
  const location = useLocation()
  const { state } = location || {}
  const { data } = state || {}
  const formVal = Form.useWatch() || {}
  const [brands, setBrands] = useState([])
  const [fileList, setFileList] = useState()
  const { useIncremental, typeLabel, useType = true, extras } = conf[formVal.source] || {}

  const handleConsturctTable = async (api, params) => {
    const res = await api(params)
    if (res?.status === 200) {
      const tableData = res?.data
      if (data?.incrementalType === 'snapshot') {
        const apiExcludedColumns = SnapshotExtraSchemas.map(i => i.columnName)
        tableData.First_Party = tableData.First_Party.filter(i => !apiExcludedColumns.includes(i.columnName))
      }
      setTables(Object.keys(res?.data)?.map(key => ({ title: key, tableApi: () => ({ status: 200, data: tableData?.[key] }) })))
      setTableData(tableData)
    }
  }
  const handleChangeSource = async (src, init) => {
    const { typeApi = () => {}, schemaApi, fetchDependsOnType } = conf[src] || {}
    const res = await typeApi()
    if (res?.status === 200) setTypeOptions(res?.data)
    const { name, brands } = { ...formVal }
    if (init) return
    if (schemaApi && !fetchDependsOnType) handleConsturctTable(schemaApi)
    setTables([])
    form.resetFields()
    form.setFieldsValue({ name, brands, source: src })
  }
  const handleChangeType = async type => {
    const { schemaApi, fetchDependsOnType } = conf[formVal.source] || {}
    const { name, brands, source } = { ...formVal }
    const typeName = typeOptions?.find(i => i.id === type)?.type?.toLowerCase()
    if (typeName === 'api') form.setFieldValue('isIncremental', 1)
    if (!fetchDependsOnType) return
    handleConsturctTable(schemaApi, typeName)
    setTables([])
    form.resetFields()
    form.setFieldsValue({ name, brands, source, type })
  }
  // ----- upload config -----
  const checkDiffSchema = header => {
    if (tableData.First_Party.length !== header.length) return true
    if (tableData.First_Party.some(i => !header.includes(i.columnName))) return true
    return false
  }
  const checkRegex = val => /[a-zA-Z_0-9\s]+/.test(val)
  const handleUpload = ({ file, onError, onSuccess }) => {
    const handleError = msg => {
      message.error(msg)
      setFileList([])
      return onError()
    }
    readCsvToJson(file).then(({ status, message: msg = 'message.error.uploadError', data, header }) => {
      if (!status) return message.error(ln_t(msg))
      if (header.some(i => !checkRegex(i))) return handleError(ln_t('message.error.upladHeaderFormat'))
      if (editMode) {
        if (checkDiffSchema(header)) return handleError(ln_t('message.error.uploadedSchemaIsDiff'))
      } else {
        const formatHeader = header?.map(i => ({ columnName: i }))
        setTables([{ title: 'First_Party', titleToRender: ln_t('dataSource.firstParty'), tableApi: () => ({ status: 200, data: formatHeader }) }])
        setTableData({ First_Party: formatHeader?.map(i => ({ ...i, alias: i.columnName })) })
      }
      setFileList([file])
    })
    return onSuccess()
  }

  const uploadProps = {
    accept: acceptedUploadFormat,
    maxCount: 1,
    fileList,
    onRemove: () => setFileList([]),
    customRequest: handleUpload,
    showUploadList: { showRemoveIcon: false }
  }

  const getBrands = async () => {
    const res = await fetchBrandList({ ...defParams })
    if (res?.status === 200) setBrands(res?.data)
  }
  useEffect(() => {
    getBrands()
    if (!data) return
    handleChangeSource(data?.dataSourceType?.name, true)
    handleConsturctTable(() => fetchSchema(data?.id))
  }, [])

  return (
    <>
      <Form.Item {...defConfig('name', 'input')}>
          <Input placeholder={defPlaceholder('name', 'input')} />
      </Form.Item>
      <Form.Item {...defConfig('brands', 'select', false)}>
          <Select
            showSearch
            mode="multiple"
            placeholder={defPlaceholder('brands', 'select')}
            options={brands?.map(i => ({ value: i.id, label: i.name }))}
            optionFilterProp="children"
          />
      </Form.Item>
      <Form.Item {...defConfig('source', 'select')}>
          <Select
            showSearch
            placeholder={defPlaceholder('source', 'select')}
            options={sources?.map(i => ({ value: i.name, label: i.name }))}
            optionFilterProp="children"
            onChange={e => handleChangeSource(e)}
            disabled={editMode}
          />
      </Form.Item>
      {useType && <Form.Item {...defConfig('type', 'select')} {...typeLabel ? { label: typeLabel } : {}}>
        <Select
          showSearch
          placeholder={defPlaceholder('type', 'select')}
          options={typeOptions?.map(i => ({ value: i.id, label: i?.type }))}
          optionFilterProp="children"
          onChange={e => handleChangeType(e)}
          disabled={editMode || !formVal.source}
        />
      </Form.Item>}
      {useIncremental
        ? <>
            <Form.Item {...defConfig('incremental', 'select')} initialValue={1} name="isIncremental">
              <Radio.Group
                options={[{ value: 1, label: ln_t('util.yes') }, { value: 0, label: ln_t('util.no'), disabled: formVal.type === typeOptions?.find(i => i?.type === 'API')?.id }]}
                optionType="button"
                buttonStyle="solid"
                disabled={editMode}
              />
            </Form.Item>
            {formVal.isIncremental
              ? <Form.Item {...defConfig('incrementalType', 'select')} initialValue="append">
                  <Radio.Group
                    options={[{ value: 'append', label: ln_t('dataSource.append') }, { value: 'overwrite', label: ln_t('dataSource.overwrite') }, { value: 'snapshot', label: ln_t('dataSource.snapshot') }]}
                    optionType="button"
                    buttonStyle="solid"
                    disabled={editMode}
                  />
                </Form.Item>
              : ''}
            <Form.Item
              {...defConfig('upload', 'select', !editMode)}
              valuePropName="fileList"
              getValueFromEvent={e => Array.isArray(e) ? e : e?.fileList}
            >
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />} style={{ width: '100%' }} disabled={editMode && data?.detailName === 'API'}>{ln_t('util.clickToUpload')}</Button>
              </Upload>
            </Form.Item>
          </>
        : '' }
      {extras && extras(props)}
    </>
  )
}

export default BasicSetting
